import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/inputs/input';
import { useGetTripsQuery, usePostTripsMutation } from '../../store/backendApi';
import { trackWithPlausible } from '../../utils/plausible';
import { Button } from '../../components/Button';

export interface CreateTripModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CreateTripModal = (props: CreateTripModalProps) => {
  const [tripName, setTripName] = useState<string | null>(null);
  const [postTrip, { data: postTripResponse, isLoading: postTripLoading }] = usePostTripsMutation();
  const { refetch: refetchTrips } = useGetTripsQuery('admin');
  const navigate = useNavigate();
  const [waitingForTrip, setWaitingForTrip] = useState(false);

  // Check if form is valid (trip name is provided)
  const isFormValid = useMemo(() => {
    return tripName && tripName.trim().length > 0;
  }, [tripName]);

  const createTrip = async () => {
    if (!tripName) return;

    const date = new Date().toISOString().split('T')[0];

    trackWithPlausible('trip-created');
    await postTrip({
      title: tripName,
      startDate: date,
      endDate: date,
      description: '',
      headerFileName: '',
      shareMode: 'PUBLIC',
    });
  };

  const refetchTripsUntilTripExists = (tripId: string) => {
    setWaitingForTrip(true);
    refetchTrips().then((response) => {
      if (response.data) {
        const foundTrip = response.data.some((trip) => trip.id === tripId);
        if (foundTrip) {
          // Trip found in list, redirect to trip page
          setWaitingForTrip(false);
          props.setOpen(false);
          navigate(`/trips/${tripId}`);
          return;
        }
      }
      setTimeout(() => {
        refetchTripsUntilTripExists(tripId);
      }, 500);
    });
  };

  useEffect(() => {
    if (postTripResponse) {
      const tripId = postTripResponse.tripId;
      refetchTripsUntilTripExists(tripId);
    }
  }, [postTripResponse]);

  return (
    <Dialog open={props.open} onClose={props.setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-neutral-dark/75 transition-opacity data-[closed]:opacity-0
        data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform rounded-lg bg-surface px-4 pb-4 pt-5 text-left
            shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300
            data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full
            sm:max-w-sm sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div>
              <div>
                <div className="text-center">
                  <DialogTitle as="h3" className="text-base font-semibold text-main">
                    Give your trip a name!
                  </DialogTitle>
                  <div className="mt-6">
                    <Input
                      label={''}
                      id={'trip-name'}
                      name={'trip-name'}
                      type={'text'}
                      placeholder="Our Italy trip '24"
                      setValue={setTripName}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6">
                <Button
                  variant="primary"
                  size="md"
                  disabled={postTripLoading || waitingForTrip || !isFormValid}
                  onClick={() => {
                    createTrip();
                  }}
                  loading={postTripLoading || waitingForTrip}
                  fullWidth
                >
                  {postTripLoading || waitingForTrip ? '...' : 'Create Trip'}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
